import React, { useState } from 'react'
import { useTrans } from 'hooks'
import { PaginaNode } from '@gql/graphql'
import { Breadcrumbs, Button, WContainer } from '@components/atoms'
import { RicercaNewsCard, RicercaPaginaCard } from '@components/molecules'
import { Page } from 'components/organisms'
import styles from './SearchTemplate.module.sass'

interface Props {
  pagina?: PaginaNode
  risultati?: {
    tipologia?: string
    is_accessorio?: boolean
    is_abbigliamento?: boolean
    object?: any
  }[]
  query?: String
}

const ITEMS_PER_LOAD = 6

const SearchTemplate = (props: Props) => {
  const { pagina = undefined, risultati = undefined, query = undefined } = props
  const t = useTrans()

  const emptyItem = {
    name: t('Sembra che non ci siano risultati per la tua ricerca'),
    description: t('description_empty_search_results'),
  }

  const [itemsToShow, setItemsToShow] = useState(ITEMS_PER_LOAD)

  const handleShowMore = () => {
    const firstNewElIndex = itemsToShow
    setItemsToShow((prev) => prev + ITEMS_PER_LOAD)
    setTimeout(() => {
      const firstNewEl = document.getElementsByClassName('result-item')?.[firstNewElIndex]
      firstNewEl?.getElementsByTagName('a')?.[0]?.focus()
    }, 100)
  }

  const renderResultsByType = (item) => {
    switch (item.tipologia) {
      case 'news':
        return (
          <RicercaNewsCard
            news={{
              pk: item.pk,
              titolo: item.content_auto,
              sottotitolo: item.sottotitolo || item.descrizione,
              url: item.url,
              previewImage: item.image,
              categoria: {
                titolo: item?.categoria,
              },
            }}
          />
        )
      case 'pagina':
        return (
          <RicercaPaginaCard
            pagina={{
              pk: item.pk,
              titolo: item.content_auto,
              descrizione: item.descrizione,
              url: item.url,
            }}
          />
        )
    }
  }

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <main>
        <div className={styles.head}>
          <WContainer>
            <div className={styles.headLayout}>
              <Breadcrumbs breadcrumbs={pagina.breadcrumbs} showLast />
              <h1 className={styles.title}>
                {t('Risultati di ricerca per')} “{query}”
              </h1>
            </div>
          </WContainer>
        </div>
        {risultati.length ? (
          <div className={styles.layout}>
            <WContainer>
              <div className={styles.results}>
                {risultati.slice(0, itemsToShow).map((risultato) => (
                  <div key={risultato?.object?.id} className="result-item">
                    {renderResultsByType(risultato)}
                  </div>
                ))}
              </div>
              {itemsToShow < risultati.length && (
                <div className={styles.loadMoreContainer}>
                  <Button
                    className={styles.loadMoreButton}
                    onClick={handleShowMore}
                    variant="secondary"
                    label={t(
                      Math.min(ITEMS_PER_LOAD, risultati.length - itemsToShow) === 1
                        ? 'Cerca {0} altro risultato'
                        : 'Cerca altri {0} risultati',
                      [Math.min(ITEMS_PER_LOAD, risultati.length - itemsToShow).toString()]
                    )}
                  />
                </div>
              )}
            </WContainer>
          </div>
        ) : (
          <div className={styles.emptyLayout}>
            <WContainer>
              <div className={styles.empty}>
                {!!emptyItem?.name && <h2 className={styles.emptyTitle}>{emptyItem.name}</h2>}
                {!!emptyItem?.description && (
                  <div
                    className={styles.emptyDescription}
                    dangerouslySetInnerHTML={{ __html: emptyItem.description }}
                  />
                )}
              </div>
            </WContainer>
          </div>
        )}
      </main>
    </Page>
  )
}

export default SearchTemplate
